import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  IdCard, Car, Shield, Camera, Check, ChevronRight, ChevronLeft,
  Upload, Calendar, Loader2, CheckCircle, Plus, X, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable photo upload card with both Camera + File upload options
const PhotoUploadCard = ({ label, value, loading, onUpload, onCamera, required = true, testId }) => (
  <div>
    <Label>{label} {required && '*'}</Label>
    <Card className={`border-2 border-dashed ${value ? 'border-green-500 bg-green-50' : 'border-gray-300'} transition-colors`}>
      <CardContent className="p-4" data-testid={testId}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-blue-600 py-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Uploading...</span>
          </div>
        ) : value ? (
          <div className="flex items-center justify-center gap-2 text-green-600 py-2">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Uploaded</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="flex gap-2 w-full">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 gap-1"
                onClick={onCamera}
                data-testid={testId ? `${testId}-camera-btn` : undefined}
              >
                <Camera className="h-4 w-4" />
                Take Photo
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 gap-1"
                onClick={onUpload}
                data-testid={testId ? `${testId}-upload-btn` : undefined}
              >
                <Upload className="h-4 w-4" />
                Upload File
              </Button>
            </div>
            <p className="text-xs text-gray-400">JPEG, PNG, or WebP (max 10MB)</p>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const VerificationPopup = ({ isOpen, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [isRenewal, setIsRenewal] = useState(false);

  const [licenseData, setLicenseData] = useState({
    license_number: '', date_of_birth: '', expiration_date: '', state: '',
    id_photo_front: '', id_photo_back: '', selfie_with_id: ''
  });

  const [vehicles, setVehicles] = useState([{
    vehicle_number: 1, registration_photo: '', plate_photo: '', plate_number: '',
    registration_number: '', make: '', model: '', year: '', color: '', vin: ''
  }]);

  const [insuranceData, setInsuranceData] = useState({
    insurance_photo: '', insurance_company: '', policy_number: '', expiration_date: ''
  });

  const token = localStorage.getItem('token');

  const steps = [
    { id: 1, name: "Driver's License", icon: IdCard },
    { id: 2, name: "Selfie with ID", icon: Camera },
    { id: 3, name: "Vehicle 1", icon: Car },
    { id: 4, name: "Vehicle 2 (Optional)", icon: Plus },
    { id: 5, name: "Insurance", icon: Shield }
  ];

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/verification/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.expired) {
          setIsRenewal(true);
          // Reset to step 1 for full re-verification
          setCurrentStep(1);
          return;
        }
        if (data.completed_steps?.includes('driver_license') && data.completed_steps?.includes('selfie')) {
          if (data.completed_steps?.includes('vehicle_1')) {
            if (data.completed_steps?.includes('insurance')) {
              if (data.verified) onComplete?.();
            } else setCurrentStep(5);
          } else setCurrentStep(3);
        }
      }
    } catch (error) {
      console.error('Failed to check verification status:', error);
    }
  };

  // Generic upload function used by both file browse and camera capture
  const doUpload = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum 10MB allowed.');
      return null;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Invalid image format. Use JPEG, PNG, or WebP.');
      return null;
    }
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/upload/image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    if (response.ok) {
      const result = await response.json();
      return result.url;
    }
    const error = await response.json();
    toast.error(error.detail || 'Failed to upload photo');
    return null;
  };

  // Trigger a file input (file browse)
  const triggerFileUpload = (fieldKey, setter, currentData, accept = 'image/jpeg,image/png,image/webp') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingField(fieldKey);
      setLoading(true);
      try {
        const url = await doUpload(file);
        if (url) {
          setter(prev => ({ ...prev, [fieldKey]: url }));
          toast.success('Photo uploaded!');
        }
      } catch { toast.error('Upload failed'); }
      finally { setLoading(false); setUploadingField(null); }
    };
    input.click();
  };

  // Trigger camera capture
  const triggerCamera = (fieldKey, setter, currentData, facing = 'environment') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = facing; // 'user' for front camera, 'environment' for rear
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingField(fieldKey);
      setLoading(true);
      try {
        const url = await doUpload(file);
        if (url) {
          setter(prev => ({ ...prev, [fieldKey]: url }));
          toast.success('Photo captured!');
        }
      } catch { toast.error('Capture failed'); }
      finally { setLoading(false); setUploadingField(null); }
    };
    input.click();
  };

  // Vehicle-specific upload/camera helpers
  const triggerVehicleUpload = (vehicleIndex, fieldKey) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingField(`vehicle_${vehicleIndex}_${fieldKey}`);
      setLoading(true);
      try {
        const url = await doUpload(file);
        if (url) {
          setVehicles(prev => {
            const updated = [...prev];
            updated[vehicleIndex] = { ...updated[vehicleIndex], [fieldKey]: url };
            return updated;
          });
          toast.success('Photo uploaded!');
        }
      } catch { toast.error('Upload failed'); }
      finally { setLoading(false); setUploadingField(null); }
    };
    input.click();
  };

  const triggerVehicleCamera = (vehicleIndex, fieldKey) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingField(`vehicle_${vehicleIndex}_${fieldKey}`);
      setLoading(true);
      try {
        const url = await doUpload(file);
        if (url) {
          setVehicles(prev => {
            const updated = [...prev];
            updated[vehicleIndex] = { ...updated[vehicleIndex], [fieldKey]: url };
            return updated;
          });
          toast.success('Photo captured!');
        }
      } catch { toast.error('Capture failed'); }
      finally { setLoading(false); setUploadingField(null); }
    };
    input.click();
  };

  // Insurance upload/camera
  const triggerInsuranceUpload = () => {
    triggerFileUpload('insurance_photo', setInsuranceData, insuranceData);
  };
  const triggerInsuranceCamera = () => {
    triggerCamera('insurance_photo', setInsuranceData, insuranceData);
  };

  // ---- Submit handlers ----
  const submitDriverLicense = async () => {
    if (!licenseData.license_number || !licenseData.date_of_birth || !licenseData.expiration_date || !licenseData.state) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!licenseData.id_photo_front) {
      toast.error('Please upload or take a photo of your ID');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/verification/driver-license`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(licenseData)
      });
      if (res.ok) {
        toast.success("Driver's license verified!");
        setCurrentStep(2);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Verification failed');
      }
    } catch { toast.error('Failed to submit driver license'); }
    finally { setLoading(false); }
  };

  const submitSelfie = async () => {
    if (!licenseData.selfie_with_id) {
      toast.error('Please take a selfie holding your ID');
      return;
    }
    toast.success('Selfie verified!');
    setCurrentStep(3);
  };

  const submitVehicle = async (vehicleIndex) => {
    const vehicle = vehicles[vehicleIndex];
    if (!vehicle.plate_number || !vehicle.make || !vehicle.model || !vehicle.year || !vehicle.color) {
      toast.error('Please fill in all required vehicle fields');
      return;
    }
    if (!vehicle.registration_photo || !vehicle.plate_photo) {
      toast.error('Please upload or take registration and plate photos');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/verification/vehicle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(vehicle)
      });
      if (res.ok) {
        toast.success(`Vehicle ${vehicle.vehicle_number} registered!`);
        setCurrentStep(vehicleIndex === 0 ? 4 : 5);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to register vehicle');
      }
    } catch { toast.error('Failed to submit vehicle'); }
    finally { setLoading(false); }
  };

  const addSecondVehicle = () => {
    if (vehicles.length < 2) {
      setVehicles(prev => [...prev, {
        vehicle_number: 2, registration_photo: '', plate_photo: '', plate_number: '',
        registration_number: '', make: '', model: '', year: '', color: '', vin: ''
      }]);
    }
  };

  const submitInsurance = async () => {
    if (!insuranceData.insurance_company || !insuranceData.policy_number || !insuranceData.expiration_date) {
      toast.error('Please fill in all insurance fields');
      return;
    }
    if (!insuranceData.insurance_photo) {
      toast.error('Please upload or take a photo of your insurance card');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/verification/insurance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(insuranceData)
      });
      if (res.ok) {
        toast.success('Insurance verified!');
        await completeVerification();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to verify insurance');
      }
    } catch { toast.error('Failed to submit insurance'); }
    finally { setLoading(false); }
  };

  const completeVerification = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/verification/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        toast.success('Verification complete! Your account is now fully verified.');
        onComplete?.();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to complete verification');
      }
    } catch { toast.error('Failed to complete verification'); }
    finally { setLoading(false); }
  };

  const updateVehicle = (index, field, value) => {
    setVehicles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const vi = currentStep === 3 ? 0 : 1; // current vehicle index

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="verification-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-blue-600" />
            {isRenewal ? 'Annual Verification Renewal' : 'Provider Identity & Vehicle Verification'}
          </DialogTitle>
          <DialogDescription>
            {isRenewal ? (
              <>Your verification has expired. Please complete all steps again for your annual renewal. This is required once per year.</>
            ) : (
              <>All service providers must complete identity verification once at signup, then renew annually.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {isRenewal && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="renewal-banner">
            <RefreshCw className="h-4 w-4" />
            Annual renewal required — your previous verification expired.
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg mb-4" data-testid="verification-progress">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep > step.id ? 'bg-green-500 text-white' :
                currentStep === step.id ? 'bg-blue-600 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {currentStep > step.id ? <Check className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-8 h-1 mx-1 ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Driver's License */}
        {currentStep === 1 && (
          <div className="space-y-4" data-testid="step-drivers-license">
            <h3 className="font-semibold flex items-center gap-2">
              <IdCard className="h-5 w-5 text-blue-600" />
              Driver's License Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>License Number *</Label>
                <Input value={licenseData.license_number} onChange={e => setLicenseData(p => ({...p, license_number: e.target.value}))} placeholder="Enter DLN" data-testid="license-number-input" />
              </div>
              <div>
                <Label>State *</Label>
                <select className="w-full h-10 px-3 border rounded-md" value={licenseData.state} onChange={e => setLicenseData(p => ({...p, state: e.target.value}))} data-testid="state-select">
                  <option value="">Select State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input type="date" value={licenseData.date_of_birth} onChange={e => setLicenseData(p => ({...p, date_of_birth: e.target.value}))} data-testid="dob-input" />
              </div>
              <div>
                <Label>Expiration Date *</Label>
                <Input type="date" value={licenseData.expiration_date} onChange={e => setLicenseData(p => ({...p, expiration_date: e.target.value}))} data-testid="exp-date-input" />
              </div>
            </div>

            <PhotoUploadCard
              label="Photo of ID (Front)"
              value={licenseData.id_photo_front}
              loading={loading && uploadingField === 'id_photo_front'}
              onUpload={() => triggerFileUpload('id_photo_front', setLicenseData, licenseData)}
              onCamera={() => triggerCamera('id_photo_front', setLicenseData, licenseData)}
              testId="id-front-upload"
            />
            <PhotoUploadCard
              label="Photo of ID (Back)"
              value={licenseData.id_photo_back}
              loading={loading && uploadingField === 'id_photo_back'}
              onUpload={() => triggerFileUpload('id_photo_back', setLicenseData, licenseData)}
              onCamera={() => triggerCamera('id_photo_back', setLicenseData, licenseData)}
              required={false}
              testId="id-back-upload"
            />

            <Button onClick={submitDriverLicense} disabled={loading} className="w-full" data-testid="submit-license-btn">
              {loading && !uploadingField ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Continue to Selfie <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Selfie with ID */}
        {currentStep === 2 && (
          <div className="space-y-4" data-testid="step-selfie">
            <h3 className="font-semibold flex items-center gap-2">
              <Camera className="h-5 w-5 text-blue-600" />
              Selfie Verification
            </h3>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Take a selfie while holding your ID next to your face. Make sure both your face and the ID are clearly visible.
              </p>
            </div>

            <Card className={`border-2 border-dashed ${licenseData.selfie_with_id ? 'border-green-500 bg-green-50' : 'border-gray-300'} transition-colors`}>
              <CardContent className="p-6" data-testid="selfie-upload">
                {loading && uploadingField === 'selfie_with_id' ? (
                  <div className="flex flex-col items-center gap-2 text-blue-600 py-2">
                    <Loader2 className="h-10 w-10 animate-spin" />
                    <span className="font-medium">Uploading...</span>
                  </div>
                ) : licenseData.selfie_with_id ? (
                  <div className="flex flex-col items-center gap-2 text-green-600 py-2">
                    <CheckCircle className="h-10 w-10" />
                    <span className="font-medium">Selfie uploaded</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 py-2">
                    <Camera className="h-12 w-12 text-gray-400" />
                    <p className="font-medium text-gray-600">Take Selfie with ID</p>
                    <div className="flex gap-2 w-full max-w-xs">
                      <Button type="button" variant="outline" className="flex-1 gap-1" onClick={() => triggerCamera('selfie_with_id', setLicenseData, licenseData, 'user')} data-testid="selfie-camera-btn">
                        <Camera className="h-4 w-4" /> Camera
                      </Button>
                      <Button type="button" variant="outline" className="flex-1 gap-1" onClick={() => triggerFileUpload('selfie_with_id', setLicenseData, licenseData)} data-testid="selfie-upload-btn">
                        <Upload className="h-4 w-4" /> Upload
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <ChevronLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button onClick={submitSelfie} disabled={loading || !licenseData.selfie_with_id} className="flex-1" data-testid="submit-selfie-btn">
                Continue to Vehicle <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 & 4: Vehicle Registration */}
        {(currentStep === 3 || currentStep === 4) && (
          <div className="space-y-4" data-testid={`step-vehicle-${currentStep === 3 ? 1 : 2}`}>
            <h3 className="font-semibold flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-600" />
              Vehicle {currentStep === 3 ? '1' : '2'} Registration
              {currentStep === 4 && <Badge variant="outline">Optional</Badge>}
            </h3>

            {currentStep === 4 && vehicles.length < 2 && (
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-3">You can register a second vehicle (optional)</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => setCurrentStep(5)}>Skip to Insurance</Button>
                  <Button onClick={addSecondVehicle}><Plus className="h-4 w-4 mr-2" /> Add Second Vehicle</Button>
                </div>
              </div>
            )}

            {(currentStep === 3 || (currentStep === 4 && vehicles.length >= 2)) && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Make *</Label>
                    <Input value={vehicles[vi]?.make || ''} onChange={e => updateVehicle(vi, 'make', e.target.value)} placeholder="e.g., Toyota" />
                  </div>
                  <div>
                    <Label>Model *</Label>
                    <Input value={vehicles[vi]?.model || ''} onChange={e => updateVehicle(vi, 'model', e.target.value)} placeholder="e.g., Camry" />
                  </div>
                  <div>
                    <Label>Year *</Label>
                    <Input type="number" value={vehicles[vi]?.year || ''} onChange={e => updateVehicle(vi, 'year', e.target.value)} placeholder="e.g., 2020" />
                  </div>
                  <div>
                    <Label>Color *</Label>
                    <Input value={vehicles[vi]?.color || ''} onChange={e => updateVehicle(vi, 'color', e.target.value)} placeholder="e.g., Silver" />
                  </div>
                  <div>
                    <Label>License Plate Number *</Label>
                    <Input value={vehicles[vi]?.plate_number || ''} onChange={e => updateVehicle(vi, 'plate_number', e.target.value.toUpperCase())} placeholder="e.g., ABC1234" />
                  </div>
                  <div>
                    <Label>VIN (Optional)</Label>
                    <Input value={vehicles[vi]?.vin || ''} onChange={e => updateVehicle(vi, 'vin', e.target.value.toUpperCase())} placeholder="17-character VIN" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <PhotoUploadCard
                    label="Photo of Registration"
                    value={vehicles[vi]?.registration_photo}
                    loading={loading && uploadingField === `vehicle_${vi}_registration_photo`}
                    onUpload={() => triggerVehicleUpload(vi, 'registration_photo')}
                    onCamera={() => triggerVehicleCamera(vi, 'registration_photo')}
                    testId={`vehicle-${vi}-reg-upload`}
                  />
                  <PhotoUploadCard
                    label="Photo of License Plate"
                    value={vehicles[vi]?.plate_photo}
                    loading={loading && uploadingField === `vehicle_${vi}_plate_photo`}
                    onUpload={() => triggerVehicleUpload(vi, 'plate_photo')}
                    onCamera={() => triggerVehicleCamera(vi, 'plate_photo')}
                    testId={`vehicle-${vi}-plate-upload`}
                  />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                    <ChevronLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                  <Button onClick={() => submitVehicle(vi)} disabled={loading} className="flex-1" data-testid={`submit-vehicle-${vi + 1}-btn`}>
                    {loading && !uploadingField ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    {currentStep === 3 ? 'Continue' : 'Continue to Insurance'} <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 5: Insurance */}
        {currentStep === 5 && (
          <div className="space-y-4" data-testid="step-insurance">
            <h3 className="font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Auto Insurance Verification
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Insurance Company *</Label>
                <Input value={insuranceData.insurance_company} onChange={e => setInsuranceData(p => ({...p, insurance_company: e.target.value}))} placeholder="e.g., State Farm" />
              </div>
              <div>
                <Label>Policy Number *</Label>
                <Input value={insuranceData.policy_number} onChange={e => setInsuranceData(p => ({...p, policy_number: e.target.value}))} placeholder="Enter policy number" />
              </div>
              <div className="col-span-2">
                <Label>Expiration Date *</Label>
                <Input type="date" value={insuranceData.expiration_date} onChange={e => setInsuranceData(p => ({...p, expiration_date: e.target.value}))} />
              </div>
            </div>

            <PhotoUploadCard
              label="Photo of Insurance Card"
              value={insuranceData.insurance_photo}
              loading={loading && uploadingField === 'insurance_photo'}
              onUpload={triggerInsuranceUpload}
              onCamera={triggerInsuranceCamera}
              testId="insurance-photo-upload"
            />

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCurrentStep(vehicles.length > 1 ? 4 : 3)}>
                <ChevronLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button onClick={submitInsurance} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="complete-verification-btn">
                {loading && !uploadingField ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Complete Verification <CheckCircle className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerificationPopup;
